// Hand-maintained context-size hints, used whenever a live number isn't available. For
// OpenAI/DeepSeek/Kimi/Qwen that's always (confirmed for OpenAI and DeepSeek by reading their
// API docs directly; Kimi and Qwen are unconfirmed either way after real effort, so treated the
// same as the confirmed-no cases). Gemini and Anthropic *do* return the true context size
// directly from their own API (see adapters/gemini.ts, adapters/anthropic.ts) — but only in the
// moment a provider's model list is actively being fetched (the Add/Edit Provider form). That
// number is never persisted onto the saved provider (matches maxContextTokens itself only being
// saved if you actually typed a value), so anywhere else — the provider list card, and the real
// truncation budget used when sending a message — has no live data to work with either. These
// two are here for exactly that reason, not because their API can't tell us.
//
// Checked in order, first match wins. This is deliberately a short list of current-generation
// name patterns, not an exhaustive per-model table — anything that doesn't match falls through
// to the caller's conservative fallback rather than a wrong guess.
interface ContextSizeRule {
  test: RegExp;
  tokens: number;
}

const RULES_BY_ENDPOINT: Record<string, ContextSizeRule[]> = {
  "https://api.openai.com/v1": [
    { test: /^gpt-5|^gpt-4\.1/i, tokens: 1000000 },
    { test: /^gpt-4o|^gpt-4-turbo/i, tokens: 128000 },
  ],
  "https://generativelanguage.googleapis.com/v1beta": [
    { test: /3\.5.*pro/i, tokens: 2000000 },
    // The one current exception to Gemini's otherwise-uniform large-context lineup.
    { test: /^gemini-3-flash(?!-lite)/i, tokens: 200000 },
    { test: /gemini/i, tokens: 1000000 },
  ],
  "https://api.anthropic.com/v1": [
    { test: /haiku/i, tokens: 200000 },
    { test: /claude|opus|sonnet|fable|mythos/i, tokens: 1000000 },
  ],
  "https://api.deepseek.com": [
    { test: /v4/i, tokens: 1000000 },
    // Legacy pre-V4 names (deepseek-chat/deepseek-reasoner resolved to V3.1) — kept in case an
    // existing provider still has one saved, even though DeepSeek's current lineup is V4-only.
    { test: /deepseek-chat|deepseek-reasoner/i, tokens: 128000 },
  ],
  // api.moonshot.ai is the current preset endpoint (see providerPresets.ts); api.moonshot.cn is
  // kept here too so an existing provider saved against the old .cn preset still resolves
  // correctly — same rules, both endpoints issue the same model lineup.
  "https://api.moonshot.ai/v1": [
    { test: /k3|k[4-9]/i, tokens: 1000000 },
    { test: /k2\.[6-9]/i, tokens: 256000 },
  ],
  "https://api.moonshot.cn/v1": [
    { test: /k3|k[4-9]/i, tokens: 1000000 },
    { test: /k2\.[6-9]/i, tokens: 256000 },
  ],
  // China and International (see providerPresets.ts's qwen-cn/qwen-intl split, 2026-08-12) are
  // genuinely different endpoints/accounts, but the same model lineup — same rules for both,
  // same reasoning as Kimi's .ai/.cn pair above.
  "https://dashscope.aliyuncs.com/compatible-mode/v1": [
    { test: /qwen3\.[5-9]|qwen[4-9]/i, tokens: 1000000 },
    { test: /qwen2\.5-turbo|-1m\b/i, tokens: 1000000 },
  ],
  "https://dashscope-intl.aliyuncs.com/compatible-mode/v1": [
    { test: /qwen3\.[5-9]|qwen[4-9]/i, tokens: 1000000 },
    { test: /qwen2\.5-turbo|-1m\b/i, tokens: 1000000 },
  ],
};

// Some providers spell the context size directly into the model name (Moonshot's classic
// "moonshot-v1-8k"/"-32k"/"-128k" series) — reading it straight from the string stays correct
// for variants that aren't explicitly listed above, so this is checked before the tables.
const SIZE_SUFFIX_PATTERN = /-(\d+)k\b/i;

export function estimateContextTokensForModel(endpoint: string, model: string): number | undefined {
  const sizeSuffixMatch = model.match(SIZE_SUFFIX_PATTERN);
  if (sizeSuffixMatch) {
    return Number(sizeSuffixMatch[1]) * 1000;
  }
  // Same trailing-slash normalization as providerPresets.ts's findPresetMatch, and for the same
  // reason: RULES_BY_ENDPOINT is keyed by the exact preset endpoint strings above, and a saved
  // endpoint with a trailing slash would otherwise silently miss every rule here and fall through
  // to the caller's conservative fallback even for a well-known model.
  const normalized = endpoint.replace(/\/+$/, "");
  const matched = RULES_BY_ENDPOINT[normalized]?.find((rule) => rule.test.test(model));
  return matched?.tokens;
}
